<template>
  <div class="github-panel">
    <b-tabs
      active-nav-item-class="active-nav-item"
      active-tab-class="active-tab"
      align="center"
      content-class="mt-3"
      card
      pills
      vertical
      end
      @input="showTab"
    >
      <template #tabs-start>
        <b-button
          style="margin-bottom:4px"
          :disabled="importing"
          variant="primary"
          @click="importToGithub()"
        >
          <font-awesome-icon
            v-show="importing"
            icon="hourglass-half"
          />
          <span v-show="!importing">Import to GitHub</span>
          <span v-show="importing">Done in a while</span>
        </b-button>
      </template>

      <b-tab
        active
      >
        <template #title>
          <font-awesome-icon
            icon="info-circle"
          /> Details
        </template>
        <GitHubStatus
          ref="githubStatus"
          :project="project"
          @getResourceLoad="getResourceLoad"
        />
      </b-tab>
      <template #tabs-end>
        <b-button
          style="margin-top:4px"
          :disabled="refreshCoolDown"
          variant="dark"
          @click="refreshSecretsForAzureEnvs()"
        >
          <font-awesome-icon icon="redo-alt" />
          <span v-show="!refreshingAzureEnv">Refresh GH Secrets</span>
          <span v-show="refreshingAzureEnv">Done in a while</span>
        </b-button>
        <b-button
          v-if="showAWSSecretButton"
          style="margin-top:4px"
          :disabled="refreshCoolDown"
          variant="dark"
          @click="refreshSecretsForAWSEnvs()"
        >
          <font-awesome-icon icon="redo-alt" />
          <span v-show="!refreshingAWSEnv">Refresh GH Secrets-AWS</span>
          <span v-show="refreshingAWSEnv">Done in a while</span>
        </b-button>
      </template>

      <b-tab
        id="new-repository"
        title="New Repository"
      >
        <h4>Create a New GitHub Repository</h4>
        <span v-if="readiness != 'ready' && isGithubProvisioned">
          <p>Please import your project before trying to add new repositories.</p>
        </span>
        <span v-else>
          <b-form-group
            id="repository-name-group"
            description="Characters that don't match the regex [A-Za-z0-9_.-] are turned into dashes."
            :valid-feedback="validFeedback"
            :invalid-feedback="invalidFeedback"
            :state="state"
          >
            <b-form-input
              id="repository-name-input"
              v-model="repositoryNameInput"
              :state="state"
              placeholder="Enter a repository name"
              style="width: 430px; margin: 0 auto"
            />
          </b-form-group>
          <b-button
            variant="success"
            :disabled="inProgress"
            @click="createRepository"
          >
            <b-spinner
              v-show="inProgress"
              small
            />
            <span v-show="!inProgress">Create</span>
            <span v-show="inProgress">Creating...</span>
          </b-button>
        </span>
      </b-tab>
    </b-tabs>
  </div>
</template>

<script>
import api from '@/api';
import {getRepositoryName} from '../utils/github';
import GitHubStatus from '@/components/GitHubStatus.vue';

export default {
  name: 'GitHubPanel',
  components: {
    GitHubStatus,
  },
  props: {
    project: {
      type: Object,
      required: true,
      validator: (value) => !!value.projectName,
    },
  },
  data() {
    return {
      inProgress: false,
      importing: false,
      readiness: 'unknown',
      repositoryNameInput: '',
      refreshingAzureEnv: false,
      refreshingAWSEnv: false,
      refreshCoolDown: false,
      resourceStatus: {},
      isGithubProvisioned: false,
      showAWSSecretButton: false,
    };
  },
  computed: {
    state() {
      return this.isValid();
    },
    invalidFeedback() {
      return '';
    },
    validFeedback() {
      const repositoryName = getRepositoryName(this.repositoryNameInput);
      return `The repository's name will be: ${this.project.projectName}_${repositoryName}`;
    },
  },
  beforeDestroy() {
    if (this.pollingReadiness) {
      clearInterval(this.pollingReadiness);
    }
  },
  methods: {
    async getResourceLoad({project, team}) {
      this.isGithubProvisioned = !project || !team;
    },
    // https://bootstrap-vue.org/docs/components/tabs#comp-ref-b-tabs-events
    async showTab(tabIndex) {
      // Fetch readiness every time a tab is shown or switched.
      if (this.project.awsEnvironments.length) {
        this.showAWSSecretButton = true;
      } else {
        this.showAWSSecretButton = false;
      }
      await this.getReadiness();
    },
    async createRepository() {
      if (!this.inProgress && this.isValid()) {
        this.inProgress = true;
        this.refreshCoolDown = true;
        const repositoryName = getRepositoryName(this.repositoryNameInput);
        await api.createGithubRepository(this.project.projectName, `${repositoryName}`);
        this.repositoryNameInput = '';
        this.inProgress = false;
        this.$bvToast.toast(
            `Repository name: ${this.project.projectName}_${repositoryName}`,
            {
              title: 'Creating Repository',
              toaster: 'b-toaster-bottom-center',
              solid: true,
              appendToast: true,
              variant: 'success',
            },
        );
        this.$refs.githubStatus.getResources();
      }
    },
    async getReadiness() {
      this.readiness = await api.getGithubProjectReadiness(this.project.projectName);
      this.importing = this.readiness == 'importing';
      if (this.pollingReadiness && this.readiness != 'importing') {
        // If we are polling and we're not running an import, stop polling.
        clearInterval(this.pollingReadiness);
        this.pollingReadiness = null;
      }
      if (!this.pollingReadiness && this.readiness == 'importing') {
        // If we are not polling and we are running an import, start polling for the import to finish.
        this.pollingReadiness = setInterval(this.getReadiness, 8000);
      }
    },
    async importToGithub() {
      await this.getReadiness();
      if (this.readiness == 'reprovision') {
        this.$bvToast.toast(
            `This project must be reprovisioned before importing to GitHub.  Click "Edit" and "Update Project" to reprovision.`,
            {
              title: 'Cannot import',
              toaster: 'b-toaster-bottom-center',
              solid: true,
              appendToast: true,
              variant: 'danger',
            },
        );
      } else {
        this.importing = true;
        this.refreshCoolDown = true;
        await api.githubImport(this.project.projectName);
        // Give it a few seconds, then start trying to resolve the outcome.
        setTimeout(this.getReadiness, 8000);
      }
    },
    async refreshSecretsForAzureEnvs() {
      await this.getReadiness();
      if (this.readiness != 'ready' && this.isGithubProvisioned) {
        this.$bvToast.toast(
            `Please import your project before trying to renew github secrets.`,
            {
              title: 'Cannot renew secrets',
              toaster: 'b-toaster-bottom-center',
              solid: true,
              appendToast: true,
              variant: 'danger',
            },
        );
      } else {
        this.refreshingAzureEnv = true;
        this.refreshCoolDown = true;
        await api.refreshGithubSecretsForAzureEnvs(this.project.projectName);
        this.refreshingAzureEnv = false;
      }
    },
    async refreshSecretsForAWSEnvs() {
      await this.getReadiness();
      if (this.readiness != 'ready' && this.isGithubProvisioned) {
        this.$bvToast.toast(
            `Please import your project before trying to renew github secrets.`,
            {
              title: 'Cannot renew secrets',
              toaster: 'b-toaster-bottom-center',
              solid: true,
              appendToast: true,
              variant: 'danger',
            },
        );
      } else {
        this.refreshingAWSEnv = true;
        this.refreshCoolDown = true;
        await api.refreshGithubSecretsForAWSEnvs(this.project.projectName);
        this.refreshingAWSEnv = false;
      }
    },
    isValid() {
      return this.repositoryNameInput.length >= 1;
    },
  },
};
</script>

<style>
.active-nav-item {
  background-color: #405467 !important;
  text-decoration: none !important;
}
.github-panel .nav-item a {
  color: black;
}
.github-panel .nav-item:hover {
  font-weight: 500;
}
</style>

<style scoped>
.github-panel {
  margin: 0;
}
#repository-name-group {
  position: relative;
  left: 50%;
  transform: translateX(-50%);
  text-align: center;
}
#new-repository {
  text-align: center;
}
</style>
